import { z } from 'zod';
import {
  CASE_EVENT_KINDS,
  CASE_KINDS,
  CASE_STATUSES,
  DISCIPLINE_KINDS,
  MAX_PULSE_QUESTIONS,
  PULSE_KEY_PATTERN,
  PULSE_SCALE,
} from './relations.js';

/** مخططات علاقات الموظفين. */

const Cuid = z.string().regex(/^[a-z0-9]{20,32}$/i, 'معرّف غير صالح.');

const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();

const dateOnly = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/, 'التاريخ بصيغة سنة-شهر-يوم.');

const optionalDateOnly = dateOnly
  .nullable()
  .optional()
  .or(z.literal('').transform(() => null));

const money = z.coerce.number().min(0).max(99_999_999_999.99);

/* ==========================================================================
   الحالة
   ========================================================================== */

/**
 * فتحُ حالة.
 *
 * الوصفُ مطلوبٌ بطول: حالةٌ مكتوبةٌ بسطرٍ واحدٍ لا يُبنى عليها تحقيقٌ ولا
 * قرار، ومن يقرؤها بعد شهرين لا يعرف ما وقع. والمخالفةُ تلزمها جهةٌ
 * تُنسب إليها — مخالفةٌ بلا منسوبٍ إليه لا معنى لها.
 */
export const CreateCaseSchema = z
  .object({
    kind: z.enum(CASE_KINDS),
    subject: z.string().trim().min(5, 'عنوان الحالة قصير.').max(200),
    description: z
      .string()
      .trim()
      .min(20, 'اكتب ما وقع بما يكفي ليُقرأ بعد شهرين.')
      .max(8000),
    raisedById: Cuid.nullable().optional(),
    aboutId: Cuid.nullable().optional(),
    assignedToId: Cuid.nullable().optional(),
    confidential: z.boolean().default(false),
    severity: z.coerce
      .number()
      .int()
      .min(1, 'الشدّة من واحدٍ إلى ثلاثة.')
      .max(3, 'الشدّة من واحدٍ إلى ثلاثة.')
      .default(2),
    openedOn: dateOnly,
    dueOn: optionalDateOnly,
  })
  .refine((v) => v.kind !== 'MISCONDUCT' || Boolean(v.aboutId), {
    message: 'المخالفةُ تُنسب إلى موظّف — اختر عمّن هي.',
    path: ['aboutId'],
  });
export type CreateCaseInput = z.infer<typeof CreateCaseSchema>;

export const UpdateCaseSchema = z.object({
  subject: z.string().trim().min(5, 'عنوان الحالة قصير.').max(200).optional(),
  description: z
    .string()
    .trim()
    .min(20, 'اكتب ما وقع بما يكفي ليُقرأ بعد شهرين.')
    .max(8000)
    .optional(),
  assignedToId: Cuid.nullable().optional(),
  confidential: z.boolean().optional(),
  severity: z.coerce.number().int().min(1).max(3).optional(),
  dueOn: optionalDateOnly,
});
export type UpdateCaseInput = z.infer<typeof UpdateCaseSchema>;

/**
 * نقلُ الحالة.
 *
 * القرارُ مطلوبٌ عند «صدر فيها قرار»، وسببُ الإغلاق عند الإغلاق: ملفٌّ
 * يُغلق بكلمةٍ واحدةٍ لا يصلح سندًا لأحد.
 */
export const CaseStatusSchema = z
  .object({
    status: z.enum(CASE_STATUSES),
    note: optionalText(4000),
  })
  .refine(
    (v) =>
      v.status !== 'DECIDED' ||
      (typeof v.note === 'string' && v.note.trim().length >= 20),
    {
      message: 'اكتب القرارَ وسببَه — لا كلمةً واحدة.',
      path: ['note'],
    },
  )
  .refine(
    (v) =>
      (v.status !== 'CLOSED' && v.status !== 'WITHDRAWN') ||
      (typeof v.note === 'string' && v.note.trim().length >= 10),
    {
      message: 'اكتب لماذا أُغلقت.',
      path: ['note'],
    },
  );
export type CaseStatusInput = z.infer<typeof CaseStatusSchema>;

export const CasesQuerySchema = z.object({
  kind: z.enum(CASE_KINDS).optional(),
  status: z.enum(CASE_STATUSES).optional(),
  aboutId: Cuid.optional(),
  q: z.string().trim().max(120).optional(),
});
export type CasesQuery = z.infer<typeof CasesQuerySchema>;

/**
 * واقعةٌ تُضاف إلى السجلّ.
 *
 * ولا يُقبل من نوعها إلّا ما يكتبه إنسان: «قرار» و«تغيّرت الحالة» يكتبهما
 * النظامُ عن واقعةٍ وقعت، وكتابتُهما بيدٍ تجعل السجلَّ يقول إنّ قرارًا صدر
 * ولا شيءَ في النظام يشهد أنّه صدر.
 */
export const AddCaseEventSchema = z.object({
  kind: z.enum(CASE_EVENT_KINDS).default('NOTE'),
  body: z.string().trim().min(5, 'اكتب ما جرى.').max(8000),
  personId: Cuid.nullable().optional(),
  assetId: z.string().trim().max(80).nullable().optional(),
});
export type AddCaseEventInput = z.infer<typeof AddCaseEventSchema>;

/* ==========================================================================
   الإجراءات التأديبية
   ========================================================================== */

export const CreateActionSchema = z
  .object({
    employeeId: Cuid,
    caseId: Cuid.nullable().optional(),
    kind: z.enum(DISCIPLINE_KINDS),
    reason: z
      .string()
      .trim()
      .min(20, 'اكتب ما وقع بما يكفي ليُقرأ في نزاع.')
      .max(4000),
    issuedOn: dateOnly,
    effectiveFrom: optionalDateOnly,
    days: z.coerce
      .number()
      .int('الأيّام رقم صحيح.')
      .min(1, 'الأيّام واحدٌ فأعلى.')
      .max(365)
      .nullable()
      .optional(),
    amount: money.nullable().optional(),
    expiresOn: optionalDateOnly,
    notes: optionalText(2000),
  })
  .refine((v) => v.kind !== 'DEDUCTION' || (v.amount ?? 0) > 0, {
    message: 'الخصمُ مبلغٌ — اكتبه.',
    path: ['amount'],
  })
  .refine((v) => v.kind !== 'SUSPENSION' || (v.days ?? 0) > 0, {
    message: 'الوقفُ مدّةٌ — اكتب أيّامَها.',
    path: ['days'],
  });
export type CreateActionInput = z.infer<typeof CreateActionSchema>;

export const AcknowledgeActionSchema = z.object({
  notes: optionalText(2000),
});
export type AcknowledgeActionInput = z.infer<typeof AcknowledgeActionSchema>;

/** تنفيذُ الخصم في المرتّب: شهرُ الاستحقاق وحدَه — والمبلغُ من الإجراء. */
export const PostDeductionSchema = z.object({
  period: dateOnly,
});
export type PostDeductionInput = z.infer<typeof PostDeductionSchema>;

export const ActionsQuerySchema = z.object({
  employeeId: Cuid.optional(),
  kind: z.enum(DISCIPLINE_KINDS).optional(),
  caseId: Cuid.optional(),
});
export type ActionsQuery = z.infer<typeof ActionsQuerySchema>;

/* ==========================================================================
   قياسُ الرضا
   ========================================================================== */

const pulseQuestions = z
  .array(
    z.object({
      key: z
        .string()
        .trim()
        .toUpperCase()
        .regex(
          PULSE_KEY_PATTERN,
          'مفتاح السؤال حروفٌ إنجليزيّةٌ كبيرةٌ وأرقامٌ وشرطةٌ سفليّة.',
        ),
      text: z.string().trim().min(5, 'نصّ السؤال قصير.').max(300),
    }),
  )
  .min(1, 'اكتب سؤالًا واحدًا على الأقلّ.')
  .max(MAX_PULSE_QUESTIONS, `الأسئلة أكثر من ${MAX_PULSE_QUESTIONS}.`)
  .refine(
    (list) => new Set(list.map((q) => q.key)).size === list.length,
    'مفتاحُ سؤالٍ مكرّر.',
  );

export const UpsertSurveySchema = z.object({
  title: z.string().trim().min(3, 'اسم الاستبيان قصير.').max(160),
  questions: pulseQuestions,
  opensOn: dateOnly,
  closesOn: optionalDateOnly,
  notes: optionalText(2000),
});
export type UpsertSurveyInput = z.infer<typeof UpsertSurveySchema>;

export const SurveyStateSchema = z.object({
  isOpen: z.boolean(),
});
export type SurveyStateInput = z.infer<typeof SurveyStateSchema>;

/**
 * إجابةٌ على استبيان.
 *
 * لا معرّفَ لصاحبها ولا يُقبل: الرضا لا يُقاس بأسماء، وحقلٌ يحمل اسمًا —
 * حتى لو لم يُعرض — يجعل من يجيب يعرف أنّه معروف، فيجيب بما يُرضي.
 */
export const PulseAnswerSchema = z.object({
  department: optionalText(120),
  scores: z.record(z.coerce.number().int().min(1).max(PULSE_SCALE)),
  comment: optionalText(2000),
});
export type PulseAnswerInput = z.infer<typeof PulseAnswerSchema>;
